import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Package, Briefcase } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useSettings, formatMoney } from "../lib/settings";
import StatusBadge from "../components/StatusBadge";
import PragmaSelect from "../components/PragmaSelect";

const engagementLabel = (t) =>
  t === "one_off" ? "One-Off" : t === "recurring" ? "Recurring" : "—";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currency } = useSettings();
  const [client, setClient] = useState(null);
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
      if (data.company_id) {
        try {
          const { data: co } = await api.get(`/companies/${data.company_id}`);
          setCompany(co);
        } catch { setCompany(null); }
      }
    } catch { toast.error("Client not found"); navigate("/clients"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (data) => {
    setSaving(true);
    try {
      const { data: updated } = await api.patch(`/clients/${id}`, data);
      setClient(updated);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  if (!client) return <div className="px-8 py-12 text-mute mono text-xs">loading…</div>;

  const deals = client.deals || [];
  const allDeliverables = [];
  for (const d of deals) {
    for (const it of (d.deliverables || [])) {
      allDeliverables.push({ ...it, _deal_name: d.deal_name });
    }
  }

  return (
    <div data-testid="client-detail-page">
      <div className="px-8 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/clients")} className="pg-btn pg-btn-ghost"><ArrowLeft size={14} /> Back</button>
          <div>
            <h1 className="text-2xl" style={{ fontWeight: 600 }}>{client.company_name || "Client"}</h1>
            <div className="mono text-[11px] text-mute mt-1">
              {client.deal_count} engagement{client.deal_count === 1 ? "" : "s"} · {formatMoney(client.total_value, currency.symbol)} total
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PragmaSelect
            value={client.status || "active"}
            onChange={(v) => patch({ status: v })}
            disabled={saving}
            options={[
              { value: "active", label: "Active" },
              { value: "paused", label: "Paused" },
              { value: "ended",  label: "Ended" },
            ]}
            testId="client-status"
            size="sm"
            style={{ minWidth: 140 }}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 p-8">
        <div className="col-span-2 space-y-6">
          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-4">
              <Briefcase size={14} className="inline mr-2 -mt-0.5" />
              Engagements ({deals.length})
            </h3>
            {deals.length === 0 ? (
              <div className="text-mute mono text-xs">No engagements yet.</div>
            ) : (
              <div className="space-y-3">
                {deals.map((d, idx) => (
                  <Link
                    key={idx}
                    to={`/deals/${d.deal_id}`}
                    className="block pg-card p-3 hover:border-purple-500"
                    style={{ background: "var(--pg-surface-2)" }}
                    data-testid={`client-deal-${d.deal_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div style={{ color: "var(--pg-text)" }}>{d.deal_name}</div>
                        <div className="mono text-[11px] text-mute mt-0.5">
                          {engagementLabel(d.engagement_type)}
                          {d.engagement_end_date && ` · ends ${(d.engagement_end_date || "").slice(0, 10)}`}
                          {" · "}{formatMoney(d.value, currency.symbol)}
                          {" · "}{(d.deliverables || []).length} item{(d.deliverables || []).length === 1 ? "" : "s"}
                        </div>
                      </div>
                      <span className="mono text-[11px] text-mute">won {(d.won_at || "").slice(0, 10)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-4">
              <Package size={14} className="inline mr-2 -mt-0.5" />
              All Deliverables ({allDeliverables.length})
            </h3>
            {allDeliverables.length === 0 ? (
              <div className="text-mute mono text-xs">No deliverables attached to any engagement yet.</div>
            ) : (
              <div className="space-y-2">
                {allDeliverables.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3" style={{ background: "var(--pg-surface-2)", border: "1px solid var(--pg-border)" }}>
                    <div className="flex-1">
                      <div className="text-sm" style={{ color: "var(--pg-text)" }}>{it.name}</div>
                      <div className="mono text-[11px] text-mute mt-0.5">
                        {it.category || "general"} · qty {it.quantity || 1} × {formatMoney(it.price, currency.symbol)} = {formatMoney(Number(it.price || 0) * Number(it.quantity || 1), currency.symbol)}
                        <span className="text-mute"> · from {it._deal_name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-4">Servicing Notes</h3>
            <textarea
              className="pg-input"
              rows={5}
              value={client.notes || ""}
              onChange={(e) => setClient({ ...client, notes: e.target.value })}
              onBlur={(e) => patch({ notes: e.target.value })}
              placeholder="Internal notes about how this client is being serviced…"
              data-testid="client-notes"
            />
          </div>
        </div>

        <div className="space-y-6">
          {company && (
            <div className="pg-card p-6">
              <h3 className="text-sm mono uppercase text-mute tracking-wider mb-3">Company</h3>
              <Link to={`/companies/${company.id}`} className="block">
                <div style={{ color: "var(--pg-text)" }}>{company.name}</div>
                <div className="mono text-[11px] text-mute mt-1">{company.domain || "no domain"}</div>
                {company.source && (
                  <div className="mono text-[11px] text-mute mt-1">Source: {company.source}</div>
                )}
              </Link>
            </div>
          )}

          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-3">Status</h3>
            <StatusBadge
              color={client.status === "active" ? "teal" : client.status === "paused" ? "yellow" : "grey"}
              label={client.status || "active"}
            />
            <div className="mono text-[11px] text-mute mt-3">
              Client since {(client.created_at || "").slice(0, 10)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
